import { isEmpty } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { LABEL_SITE_NAME, PAGE_SITE } from './InfrastructureConstants';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { PageLink } from '../../links/Links';
import useRecordContext from '../../../utilities/RecordContextProviderUtilities';
import { EMPTY_ARRAY } from '../../../utilities/ConstantsUtilities';
import { useApiDataFetch } from '../../../utilities/Hooks';

/**
 * Renders a list of site links for a device
 * @param {Object} props - Component props
 * @param {Array} props.sites - Array of site objects with _id and name properties
 * @returns {JSX.Element} List of site links
 */
export const DeviceSitesList = ({ sites }) => {
    if (!sites || sites.length === 0) {
        return <>{DEFAULT_INVALID_VALUE}</>;
    }

    return (
        <div>
            {sites.map(({ _id, name }) => (
                <div key={_id}>
                    <PageLink target={PAGE_SITE} targetName={name} filters={[{ name: LABEL_SITE_NAME, uuid: _id }]} />
                </div>
            ))}
        </div>
    );
};

DeviceSitesList.propTypes = {
    sites: PropTypes.arrayOf(
        PropTypes.shape({
            _id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
};

const MemoizedDeviceSitesList = React.memo(DeviceSitesList);

/**
 * Fetches and displays sites for a specific device
 * @param {Object} props - Component props
 * @param {string} props.deviceId - Device ID to fetch sites for
 * @returns {JSX.Element} Device sites list or fallback value
 */
const DeviceSitesById = ({ deviceId }) => {
    const sites = useApiDataFetch({
        endpoint: `/ngp/v2/devices/${deviceId}`,
        selector: (data) => data?.sites || EMPTY_ARRAY,
        initialState: EMPTY_ARRAY,
        dependencies: [deviceId],
    });

    return <MemoizedDeviceSitesList sites={sites} />;
};

DeviceSitesById.propTypes = {
    deviceId: PropTypes.string.isRequired,
};

/**
 * Main component that retrieves device ID from context and displays its sites
 * @returns {JSX.Element} Device sites or fallback value
 */
const SitesLink = () => {
    const { id: recordId } = useRecordContext();

    if (isEmpty(recordId)) {
        return <>{DEFAULT_INVALID_VALUE}</>;
    }

    return <DeviceSitesById deviceId={recordId} />;
};

export default SitesLink;
